/**
 * C-1 — locale durumu: seçilen dil + kalıcılık (localStorage) + <html lang/dir> yansıması.
 * İlk değer: saklanan dil, yoksa DEFAULT_LOCALE. Zustand (auth session store deseni).
 *
 * ⚠ THE BROWSER'S LANGUAGE IS NOT CONSULTED, AND THAT IS THE POINT OF THE CURRENT RULE. It used to win
 * over the default, which made "the product opens in English" untrue for precisely the visitors most
 * likely to arrive here: a Turkish browser was handed Turkish, so the default was only ever seen by
 * people whose language we do not publish in. The product now opens in English for everyone who has not
 * chosen otherwise, and choosing is one click away in the switcher — where the choice is REMEMBERED,
 * which is the difference between a guess and a preference.
 */
import { create } from 'zustand';

import { DEFAULT_LOCALE, directionOf, Locale, normalizeLocale } from './locales';

const STORAGE_KEY = 'zeredu-locale';

function readStored(): string | null {
  // localStorage yoksa (SSR / test ortamı) ya da erişim engellenmişse sessizce atla.
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeStored(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    // yoksay: kalıcılık olmadan da locale çalışmalı
  }
}

function detectInitial(): Locale {
  // A stored value is a DECISION the visitor made in the switcher, so it outranks the default; anything
  // else — including `navigator.language` — is a guess, and a guess that overrides the product's own
  // default is indistinguishable from the default being wrong.
  return normalizeLocale(readStored()) ?? DEFAULT_LOCALE;
}

function applyToDocument(locale: Locale): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.setAttribute('lang', locale);
  document.documentElement.setAttribute('dir', directionOf(locale));
}

/**
 * Has the visitor ever chosen a language here?
 *
 * ⚠ THE QUESTION IS "IS THERE A DECISION", NOT "WHAT IS IT", and the two are not the same: a stored
 * `en` and no stored value both produce `en` from `detectInitial`, while only the first outranks a
 * site's own default. The marketing site needs the distinction — see `applyPublishedLocale` there — and
 * it must not read this key itself, because a second copy of the key is a second thing to keep in step.
 */
export function hasStoredLocale(): boolean {
  return normalizeLocale(readStored()) !== null;
}

interface LocaleState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<LocaleState>((set) => {
  const initial = detectInitial();
  applyToDocument(initial);
  return {
    locale: initial,
    setLocale: (locale) => {
      writeStored(locale);
      applyToDocument(locale);
      set({ locale });
    }
  };
});

/**
 * Switch the language WITHOUT recording a preference.
 *
 * ⚠ THE MISSING `writeStored` IS THE ENTIRE POINT, so it is not an omission to tidy up. The marketing
 * site applies its tenant's own default language with this, and that default is a fact about a SITE
 * while the storage key is a decision by a PERSON — one key, shared across every screen on this origin.
 * Persisting it would mean a visitor who only ever read a Turkish landing page finds the sign-in screen,
 * their next visit and every other Zeredu app on this origin in Turkish forever, and that the tenant
 * changing their default later reaches nobody who has already visited.
 *
 * ⚠ CALL IT BEFORE THE FIRST RENDER. It is a store write like any other: mid-render it re-renders every
 * subscriber, and after first paint it swaps the language in front of the visitor.
 */
export function applyLocale(locale: Locale): void {
  applyToDocument(locale);
  useLocaleStore.setState({ locale });
}

/** Hook-dışı erişim (ör. api-client Accept-Language başlığı). */
export function getLocale(): Locale {
  return useLocaleStore.getState().locale;
}
